// Doctor-management actions (SOW §3.5): profile edit, append-only credential edit,
// deactivate/reactivate, and standalone EMR re-sync — each a real Command write (mutate + audit
// server-side). Reloads the doctor on success. THU-354: re-skinned to the doctor edit-in-place pattern
// (settings-card / field-row / pbtn; profile read-only until "Edit"). The credential section is a
// deliberate append-only "new version" form (blank = keep current), so it stays an explicit input set.
// The wired writes (doctorWrites.edit/deactivate/reactivate/resync) are unchanged.
import { useState } from "react";
import { useAppAuth } from "../auth";
import {
  doctorWrites,
  type DoctorProfileFields,
  type DoctorCredFields,
} from "../lib/doctorWrites";

const EditIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

interface Doctor {
  id: string;
  first_name: string;
  last_name: string;
  specialty?: string | null;
  application_status?: string | null;
}

type Saving = null | "profile" | "credentials" | "approve" | "deactivate" | "reactivate" | "resync";

const CRED_FIELDS: {
  key: keyof DoctorCredFields;
  label: string;
  placeholder?: string;
  type?: string;
  maxLength?: number;
}[] = [
  { key: "npi", label: "NPI" },
  { key: "dea_number", label: "DEA" },
  { key: "license_state", label: "License state (2-letter)", placeholder: "CA", maxLength: 2 },
  { key: "license_number", label: "License #" },
  { key: "license_expires_at", label: "License expires", type: "date" },
  { key: "malpractice_carrier", label: "Malpractice carrier" },
  { key: "malpractice_policy_no", label: "Malpractice policy #" },
  { key: "malpractice_expires_at", label: "Malpractice expires", type: "date" },
];

export function DoctorActions({ doctor, onChanged }: { doctor: Doctor; onChanged: () => void }) {
  const { getToken } = useAppAuth();
  const [saving, setSaving] = useState<Saving>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [editingProfile, setEditingProfile] = useState(false);
  const [profile, setProfile] = useState<DoctorProfileFields>({
    first_name: doctor.first_name,
    last_name: doctor.last_name,
    specialty: doctor.specialty ?? "",
  });
  const [creds, setCreds] = useState<DoctorCredFields>({});

  async function run(kind: Saving, fn: () => Promise<unknown>, ok: string) {
    setSaving(kind);
    setError(null);
    setFlash(null);
    try {
      await fn();
      setFlash(ok);
      if (kind === "credentials") setCreds({}); // reset the "new version" form after a save
      onChanged();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
      return false;
    } finally {
      setSaving(null);
    }
  }

  const profilePayload = (): DoctorProfileFields => {
    const out: DoctorProfileFields = {};
    if (profile.first_name && profile.first_name !== doctor.first_name) out.first_name = profile.first_name;
    if (profile.last_name && profile.last_name !== doctor.last_name) out.last_name = profile.last_name;
    if ((profile.specialty ?? "") !== (doctor.specialty ?? "")) out.specialty = profile.specialty;
    return out;
  };
  const profileDirty = Object.keys(profilePayload()).length > 0;

  // Only send credential fields that were actually filled in (a new versioned row is created).
  const credsPayload = (): DoctorCredFields =>
    Object.fromEntries(Object.entries(creds).filter(([, v]) => v && v.trim() !== ""));
  const credsDirty = Object.keys(credsPayload()).length > 0;

  function startEdit() {
    setProfile({ first_name: doctor.first_name, last_name: doctor.last_name, specialty: doctor.specialty ?? "" });
    setError(null);
    setFlash(null);
    setEditingProfile(true);
  }
  function cancelEdit() {
    setProfile({ first_name: doctor.first_name, last_name: doctor.last_name, specialty: doctor.specialty ?? "" });
    setError(null);
    setEditingProfile(false);
  }
  async function saveProfile() {
    const ok = await run("profile", () => doctorWrites.edit(getToken, doctor.id, { profile: profilePayload() }), "Profile updated.");
    if (ok) setEditingProfile(false);
  }

  return (
    <>
      {/* Profile — read-only until Edit. */}
      <div className="settings-card">
        <div className="pcard-head">
          <h3>Profile</h3>
          {!editingProfile && (
            <button className="pbtn pbtn-ghost pbtn-sm" onClick={startEdit}>{EditIcon} Edit</button>
          )}
        </div>
        {flash && saving === null && !editingProfile && <div className="state">{flash}</div>}
        <div className="field-row">
          <div className="field">
            <label className="field-label">First name</label>
            <input className="field-input" value={profile.first_name ?? ""} readOnly={!editingProfile} disabled={!editingProfile} onChange={(e) => setProfile((f) => ({ ...f, first_name: e.target.value }))} />
          </div>
          <div className="field">
            <label className="field-label">Last name</label>
            <input className="field-input" value={profile.last_name ?? ""} readOnly={!editingProfile} disabled={!editingProfile} onChange={(e) => setProfile((f) => ({ ...f, last_name: e.target.value }))} />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label className="field-label">Specialty</label>
            <input className="field-input" value={profile.specialty ?? ""} readOnly={!editingProfile} disabled={!editingProfile} onChange={(e) => setProfile((f) => ({ ...f, specialty: e.target.value }))} />
          </div>
          <div className="field" />
        </div>
        {editingProfile && (
          <div className="form-actions">
            {error && <span className="save-error" role="alert">{error}</span>}
            <button className="pbtn pbtn-ghost" disabled={saving !== null} onClick={cancelEdit}>Cancel</button>
            <button className="pbtn pbtn-primary" disabled={saving !== null || !profileDirty} onClick={() => void saveProfile()}>
              {saving === "profile" ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>

      {/* Credentials — append-only new version (blank = keep current). */}
      <div className="settings-card">
        <div className="pcard-head">
          <h3>Credentials <em>— new version</em></h3>
        </div>
        <p className="muted" style={{ marginTop: -6, marginBottom: 14 }}>
          Leave a field blank to keep the current value. Saving creates a new versioned record.
        </p>
        {error && saving === "credentials" && <div className="state state--error">{error}</div>}
        <fieldset className="cred-grid" disabled={saving !== null}>
          {CRED_FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label className="field-label">{f.label}</label>
              <input
                className="field-input"
                type={f.type ?? "text"}
                maxLength={f.maxLength}
                placeholder={f.type === "date" ? undefined : (f.placeholder ?? "")}
                value={creds[f.key] ?? ""}
                onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
              />
            </div>
          ))}
        </fieldset>
        <div className="form-actions">
          {flash && saving === null && <span className="saved-note">{flash}</span>}
          <button
            className="pbtn pbtn-primary"
            disabled={saving !== null || !credsDirty}
            onClick={() => void run("credentials", () => doctorWrites.edit(getToken, doctor.id, { credentials: credsPayload() }), "New credential version saved.")}
          >
            {saving === "credentials" ? "Saving…" : "Save credentials"}
          </button>
        </div>
      </div>

      {/* Lifecycle + EMR. */}
      <div className="settings-card">
        <div className="pcard-head"><h3>Lifecycle</h3></div>
        <div className="form-actions" style={{ borderTop: "none", paddingTop: 0, marginTop: 0, justifyContent: "flex-start" }}>
          {doctor.application_status !== "approved" && (
            <button className="pbtn pbtn-primary" disabled={saving !== null} onClick={() => void run("approve", () => doctorWrites.approve(getToken, doctor.id), "Doctor approved — now bookable on patient.dev.")}>
              {saving === "approve" ? "Approving…" : "Approve"}
            </button>
          )}
          <button className="pbtn pbtn-danger" disabled={saving !== null} onClick={() => void run("deactivate", () => doctorWrites.deactivate(getToken, doctor.id), "Doctor deactivated.")}>
            {saving === "deactivate" ? "…" : "Deactivate"}
          </button>
          <button className="pbtn pbtn-ghost" disabled={saving !== null} onClick={() => void run("reactivate", () => doctorWrites.reactivate(getToken, doctor.id), "Doctor reactivated.")}>
            {saving === "reactivate" ? "…" : "Reactivate"}
          </button>
          <button className="pbtn pbtn-primary" disabled={saving !== null} onClick={() => void run("resync", () => doctorWrites.resync(getToken, doctor.id), "Re-synced to EMR.")}>
            {saving === "resync" ? "Syncing…" : "Re-sync to EMR"}
          </button>
        </div>
        {/* Surface the lifecycle action result HERE, next to the buttons — otherwise a failure
            (e.g. 409 "has upcoming visits") only shows in the profile card up top and the click
            looks like a no-op. */}
        {error && <div className="state state--error" role="alert" style={{ marginTop: 12 }}>{error}</div>}
        {flash && saving === null && <div className="state" style={{ marginTop: 12 }}>{flash}</div>}
      </div>
    </>
  );
}
